import { Check } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { CoverageValues } from '../types'
import {
  HAVARIJNI_ROZSAH,
  HAVARIJNI_SPOLUUCAST_FIX,
  HAVARIJNI_SPOLUUCAST_PCT,
  HAVARIJNI_UZEMI,
  POVINNE_LIMITY,
  SKLA_LIMIT,
  SKLA_ROZSAH,
  SKLA_SPOLUUCAST,
  URAZOVE_LIMIT,
  URAZOVE_ROZSAH,
} from '../types'

interface Props {
  value: CoverageValues
  onChange: (next: CoverageValues) => void
}

const EMPTY_VALS = new Set(['', '-'])

export function CoverageForm({ value, onChange }: Props) {
  function setField<K extends keyof CoverageValues>(key: K, v: CoverageValues[K]) {
    onChange({ ...value, [key]: v })
  }

  // HAV deductible is either percentage OR fixed amount — never both. Picking a
  // real value in one field resets the other (matches Petrisk behaviour).
  function setHavSpoluucast(which: 'percent' | 'fix', v: string) {
    if (which === 'percent') {
      onChange({ ...value, 'havarijni-spoluucast-percent': v, ...(v !== '-' ? { 'havarijni-spoluucast-fix': '-' } : null) })
    } else {
      onChange({ ...value, 'havarijni-spoluucast-fix': v, ...(v !== '-' ? { 'havarijni-spoluucast-percent': '-' } : null) })
    }
  }

  return (
    <div className="divide-y divide-zinc-100 rounded-md border border-zinc-100">
      <Row label="Povinné ručení" active={!EMPTY_VALS.has(value['povinne-limit'] ?? '') && value['povinne-limit'] !== 'Bez pojištění'}>
        <Field label="Limit">
          <Select value={value['povinne-limit'] ?? ''} onChange={(v) => setField('povinne-limit', v)} options={POVINNE_LIMITY} />
        </Field>
      </Row>

      <Row label="Havarijní pojištění" active={isPicked(value['havarijni-rozsah'])}>
        <Field label="Rozsah">
          <Select placeholder="Vyberte rozsah" value={value['havarijni-rozsah'] ?? '-'} onChange={(v) => setField('havarijni-rozsah', v)} options={HAVARIJNI_ROZSAH} />
        </Field>
        <Field label="Území">
          <Select placeholder="Vyberte území" value={value['havarijni-uzemi'] ?? '-'} onChange={(v) => setField('havarijni-uzemi', v)} options={HAVARIJNI_UZEMI} />
        </Field>
        <Field label="Spoluúčast %">
          <Select placeholder="Vyberte" value={value['havarijni-spoluucast-percent'] ?? '-'} onChange={(v) => setHavSpoluucast('percent', v)} options={HAVARIJNI_SPOLUUCAST_PCT} />
        </Field>
        <Field label="Spoluúčast fix">
          <Select placeholder="Vyberte" value={value['havarijni-spoluucast-fix'] ?? '-'} onChange={(v) => setHavSpoluucast('fix', v)} options={HAVARIJNI_SPOLUUCAST_FIX} />
        </Field>
      </Row>

      <Row label="Pojištění skel" active={isPicked(value['skla-rozsah'])}>
        <Field label="Rozsah">
          <Select placeholder="Vyberte rozsah" value={value['skla-rozsah'] ?? '-'} onChange={(v) => setField('skla-rozsah', v)} options={SKLA_ROZSAH} />
        </Field>
        <Field label="Limit">
          <Select placeholder="Vyberte limit" value={value['skla-limit'] ?? '-'} onChange={(v) => setField('skla-limit', v)} options={SKLA_LIMIT} />
        </Field>
        <Field label="Spoluúčast">
          <Select placeholder="Vyberte" value={value['skla-spoluucast'] ?? '-'} onChange={(v) => setField('skla-spoluucast', v)} options={SKLA_SPOLUUCAST} />
        </Field>
      </Row>

      <Row label="Úrazové pojištění" active={isPicked(value['urazove-rozsah'])}>
        <Field label="Rozsah">
          <Select placeholder="Vyberte rozsah" value={value['urazove-rozsah'] ?? '-'} onChange={(v) => setField('urazove-rozsah', v)} options={URAZOVE_ROZSAH} />
        </Field>
        <Field label="Násobek">
          <Select placeholder="Vyberte násobek" value={value['urazove-limit'] ?? '-'} onChange={(v) => setField('urazove-limit', v)} options={URAZOVE_LIMIT} />
        </Field>
      </Row>

      <Row label="Zavazadla" active={!!value['zavazadla-limit']}>
        <Field label="Limit">
          <Input className="h-8 text-xs" value={value['zavazadla-limit'] ?? ''} onChange={(e) => setField('zavazadla-limit', e.target.value)} placeholder="Zadejte limit" />
        </Field>
      </Row>

      <Row label="Srážka se zvěří" active={!!value['srazka-limit']}>
        <Field label="Limit">
          <Input className="h-8 text-xs" value={value['srazka-limit'] ?? ''} onChange={(e) => setField('srazka-limit', e.target.value)} placeholder="Zadejte limit" />
        </Field>
      </Row>

      <Row label="Náhradní vozidlo" active={!!value['nahradni-limit']}>
        <Field label="Limit">
          <Input className="h-8 text-xs" value={value['nahradni-limit'] ?? ''} onChange={(e) => setField('nahradni-limit', e.target.value)} placeholder="Zadejte limit" />
        </Field>
      </Row>

      <Row label="Strojní pojištění" active={!!value['strojni-limit']}>
        <Field label="Limit">
          <Input className="h-8 text-xs" value={value['strojni-limit'] ?? ''} onChange={(e) => setField('strojni-limit', e.target.value)} placeholder="Zadejte limit" />
        </Field>
      </Row>

      <CheckboxRow label="GAP" checked={!!value.gap} onChange={(v) => setField('gap', v)} />
      <CheckboxRow label="Živelní pojištění" checked={!!value.zivelni} onChange={(v) => setField('zivelni', v)} />
      <CheckboxRow label="Nadstandardní asistenční služby" checked={!!value.asistencni} onChange={(v) => setField('asistencni', v)} />
      <CheckboxRow label="Činnost pracovního stroje" checked={!!value.cinnost} onChange={(v) => setField('cinnost', v)} />
    </div>
  )
}

function isPicked(v?: string): boolean {
  if (!v) return false
  if (EMPTY_VALS.has(v)) return false
  if (v === 'Bez pojištění') return false
  return true
}

function Row({ label, active, children }: { label: string; active: boolean; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[1.25rem_minmax(11rem,14rem)_repeat(4,minmax(0,1fr))] items-end gap-x-3 gap-y-2 px-3 py-2.5 hover:bg-zinc-50/60">
      <span className="flex h-8 items-center justify-center" title={active ? 'Zahrnuto' : 'Nezahrnuto'}>
        {active ? (
          <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
        ) : (
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-zinc-300" />
        )}
      </span>
      <div className="flex h-8 items-center text-xs font-medium text-zinc-800">{label}</div>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <span className="mb-1 block truncate text-[10px] font-medium uppercase tracking-wide text-zinc-400">{label}</span>
      {children}
    </div>
  )
}

function Select({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder?: string
}) {
  const isEmpty = EMPTY_VALS.has(value)
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-8 w-full appearance-none rounded border border-zinc-300 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand/30',
        isEmpty ? 'text-zinc-400' : 'text-zinc-900',
      )}
    >
      {options.map((o) => (
        <option key={o} value={o} className={EMPTY_VALS.has(o) ? 'text-zinc-400' : 'text-zinc-900'}>
          {EMPTY_VALS.has(o) ? placeholder ?? '—' : o}
        </option>
      ))}
    </select>
  )
}

function CheckboxRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-zinc-50/60">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <span className="text-xs font-medium text-zinc-800">{label}</span>
    </label>
  )
}
